import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { http, errorText } from "@/lib/api";
import { Reveal, SectionLabel } from "./Reveal";

export function Newsletter() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [state, setState] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    setState("busy");
    try {
      const { data } = await http.post("/newsletter/subscribe", { email, name });
      toast.success(data?.message ?? "Subscription confirmed!");
      setState("done");
      setEmail("");
      setName("");
    } catch (err) {
      toast.error(errorText(err));
      setState("idle");
    }
  };

  return (
    <section id="newsletter" data-testid="newsletter-section" className="relative overflow-hidden py-28 md:py-40">
      <div className="pointer-events-none absolute -right-40 top-10 h-[520px] w-[520px] rounded-full bg-[var(--gold)]/10 blur-[120px]" />
      <div className="relative mx-auto grid max-w-[1440px] gap-14 px-5 md:px-10 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <Reveal><SectionLabel index="03">Mailing list</SectionLabel></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-12 font-display text-6xl font-black uppercase leading-[0.85] sm:text-7xl lg:text-9xl">
              Join <span className="font-serif-i font-normal normal-case text-[var(--gold)]">the</span><br />crew.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-md text-sm leading-relaxed text-[var(--stone)] md:text-base">
              New riddims and exclusive previews. No spam, just basslines. You'll get a welcome email right away.
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.25} className="lg:col-span-5 lg:self-end">
          <form onSubmit={submit} data-testid="newsletter-form" className="space-y-8">
            <label className="block">
              <span className="eyebrow !text-[var(--stone)]">Name</span>
              <input data-testid="newsletter-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="mt-3 w-full border-b border-white/20 bg-transparent pb-3 text-2xl outline-none transition-colors placeholder:text-white/30 focus:border-[var(--gold)]" />
            </label>
            <label className="block">
              <span className="eyebrow !text-[var(--stone)]">Email *</span>
              <input data-testid="newsletter-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="mt-3 w-full border-b border-white/20 bg-transparent pb-3 text-2xl outline-none transition-colors placeholder:text-white/30 focus:border-[var(--gold)]" />
            </label>
            <motion.button whileTap={{ scale: 0.97 }} data-testid="newsletter-submit-button" disabled={state === "busy"} className="group flex w-full items-center justify-between rounded-full bg-[var(--bone)] py-4 pl-8 pr-4 font-mono text-sm uppercase tracking-[0.2em] text-[var(--ink)] transition-colors hover:bg-[var(--gold)] disabled:opacity-70">
              {state === "done" ? "You're in" : "Join"}
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--ink)] text-[var(--bone)] transition-transform duration-500 group-hover:rotate-45">
                {state === "busy" ? <Loader2 size={16} className="animate-spin" /> : state === "done" ? <Check size={16} /> : <ArrowUpRight size={16} />}
              </span>
            </motion.button>
            <p className="text-xs text-[var(--stone)]">By subscribing you agree to receive emails from Stillworking Production. You can unsubscribe at any time.</p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
